import { z } from 'zod';
import { instrumentoSchema, tipoDeEventoSchema } from '../eventos.js';
import { respuestaSchema } from '../formularios/tipos.js';
import { actividadRecienteSchema, evaluacionDeLaVistaSchema } from '../motor/api.js';
import { colorDeReglaSchema } from '../motor/reglas.js';
import { faseSchema } from '../programa/fases.js';
/**
 * Contrato del panel del profesional · M-02, M-04, M-05 a M-10, M-23.
 *
 * **Acá no hay alertas.** Ni bandeja, ni ciclo de vida, ni escalamientos, ni
 * tiempos de resolución, ni acciones recomendadas. Fundación las eliminó el
 * 29/07/2026 y lo ratificó el 06/08 (`CLAUDE.md` §8): lo que hay es el color del
 * semáforo, visible, y la información para que el equipo decida. Los wireframes
 * M-03 y M-21 quedan sin construir, y el propio `wireframes-medico.html` los
 * marca "Sin efecto · Fundación 08/2026".
 *
 * Lo que sí viaja, y la definición que lo manda:
 *
 *   - **Color y puntaje de la última evaluación, con su fecha.** El color queda
 *     congelado entre hitos (Ezequiel, 06/08).
 *   - **Actividad reciente sin color**: días desde el último contacto y ratio de
 *     realizados sobre intentados. Reemplaza a la "adherencia %" con umbral.
 *   - **Tendencias ↑↓→** por cambio de ≥1 punto.
 *
 * Ningún campo de este contrato lleva nombre, DNI ni teléfono: el alias
 * operativo y nada más. La identidad vive en el `Patient` del store.
 */
// ── M-02 · Dashboard de cohorte ─────────────────────────────────────────────
/**
 * La distribución del semáforo.
 *
 * `sinColor` es una categoría propia y no se suma al verde: son los pacientes
 * que todavía no tienen ninguna escala medida. Meterlos en verde diría que están
 * bien, que es justamente lo que nadie observó.
 */
export const distribucionDeSemaforoSchema = z.strictObject({
    verde: z.number().int().nonnegative(),
    amarillo: z.number().int().nonnegative(),
    rojo: z.number().int().nonnegative(),
    sinColor: z.number().int().nonnegative(),
});
export const cohorteSchema = z.strictObject({
    activos: z.number().int().nonnegative(),
    semaforo: distribucionDeSemaforoSchema,
    // `partialRecord`: una institución sin ningún paciente en M12 no tiene la
    // clave, y obligarla a mandar un cero sería inventar una fila vacía.
    porFase: z.partialRecord(faseSchema, z.number().int().nonnegative()),
    /**
     * Actividad de la cohorte, **sin color**. `enSilencio` es un estado operativo
     * del protocolo, no un color del semáforo: el silencio no pinta.
     */
    actividad: z.strictObject({
        respondieronHoy: z.number().int().nonnegative(),
        enSilencio: z.number().int().nonnegative(),
        contactosDeHoy: z.number().int().nonnegative(),
    }),
});
// ── M-04 · Lista de pacientes ───────────────────────────────────────────────
export const filaDePacienteSchema = z.strictObject({
    programaId: z.uuid(),
    caseId: z.string().max(60),
    alias: z.string().max(80),
    diaDelPrograma: z.number().int().nonnegative(),
    fase: faseSchema,
    /** `null` mientras no haya escala medida. Sin color no es verde. */
    color: colorDeReglaSchema.nullable(),
    colorDesde: z.iso.datetime().nullable(),
    /** Días desde el último contacto del paciente o su cuidador. Sin color. */
    diasSinContacto: z.number().int().nonnegative().nullable(),
    /**
     * Lo abierto hoy, y si no, lo próximo: **la misma lectura que la ficha**
     * (P-90). `abierto` distingue los dos casos, y no lleva color: es un dato
     * operativo, no una alerta.
     */
    proximoHito: z
        .strictObject({
        hito: z.string().max(20),
        clase: z.string().max(20),
        enDias: z.number().int(),
        abierto: z.boolean(),
    })
        .nullable(),
    enSilencio: z.boolean(),
    /**
     * Se agotó el protocolo de silencio de su fase: la definición de "sin
     * contacto" que Fundación aprobó el 28/10 (respuesta 2.1). Operativo, sin color.
     */
    sinContacto: z.boolean(),
    /**
     * Nombre y apellido, descifrados para quien ve pacientes (ADR-020, decisión
     * 4). Ausente cuando el paciente no tiene identidad cifrada (una base de
     * desarrollo anterior al alta) o la API levantó sin claves.
     */
    identidad: z.strictObject({ nombres: z.string(), apellidos: z.string() }).optional(),
});
/** Cuántos pacientes trae cada página de la lista (06/10/2026). */
export const PACIENTES_POR_PAGINA = 20;
export const listaDePacientesSchema = z.strictObject({
    /** Todos los que cumplen los filtros, no sólo los de esta página. */
    total: z.number().int().nonnegative(),
    /** La página que vino, desde 1. */
    pagina: z.number().int().positive(),
    porPagina: z.number().int().positive(),
    pacientes: z.array(filaDePacienteSchema),
});
export const filtrosDePacientesSchema = z.strictObject({
    color: colorDeReglaSchema.or(z.literal('sin-color')).optional(),
    fase: faseSchema.optional(),
    /** Sólo los pacientes sin contacto (respuesta 2.1 de Fundación). */
    contacto: z.literal('sin-contacto').optional(),
    /**
     * Búsqueda por número de caso, por alias, y **por documento exacto o por
     * palabra completa del nombre o del apellido**, a través de las huellas
     * (ADR-020, decisión 4). No hay búsqueda por fragmento: filtra más de lo que
     * el ADR acepta.
     */
    busqueda: z.string().max(60).optional(),
    /** La página, desde 1. Una página que no existe vuelve vacía, con el total. */
    pagina: z.coerce.number().int().min(1).max(10_000).optional(),
});
// ── M-05 · Tab Contexto ─────────────────────────────────────────────────────
export const fichaDelCasoSchema = z.strictObject({
    programaId: z.uuid(),
    caseId: z.string().max(60),
    alias: z.string().max(80),
    institucion: z.string().max(120),
    diaDelPrograma: z.number().int().nonnegative(),
    totalDeDias: z.number().int().positive(),
    fase: faseSchema,
    color: colorDeReglaSchema.nullable(),
    colorDesde: z.iso.datetime().nullable(),
    colorInstrumento: instrumentoSchema.nullable(),
    /** Última evaluación de cada instrumento, con su tendencia. Vista entre hitos. */
    evaluaciones: z.array(evaluacionDeLaVistaSchema),
    actividad: actividadRecienteSchema,
    /**
     * Lo que la ficha muestra en su tarjeta de contacto: **lo que está abierto
     * hoy**, y si no hay nada abierto, lo próximo del plan (P-84). El equipo sí ve
     * los códigos de hito: son su idioma.
     */
    proximoHito: z
        .strictObject({
        hito: z.string().max(20),
        clase: z.string().max(20),
        fecha: z.iso.datetime(),
        enDias: z.number().int(),
        /** Está esperando respuesta ahora. Es un dato operativo, no una alerta. */
        abierto: z.boolean(),
        /** Desde cuándo está abierto. `null` cuando todavía no salió. */
        desdeAt: z.iso.datetime().nullable(),
        /** Cuándo cierra su ventana de respuesta. */
        venceAt: z.iso.datetime(),
    })
        .nullable(),
    /** Último reporte estructurado del propio paciente (UC-P06). */
    ultimoReporte: z
        .strictObject({ categoria: z.string().max(40), occurredAt: z.iso.datetime() })
        .nullable(),
    /**
     * Última nota del equipo: su tipo, su fecha y el comienzo de lo que dice.
     *
     * El comienzo es un adelanto, no la nota — el texto completo se lee en el tab
     * Notas. Es `null` cuando la nota está en el store y el store no respondió
     * ahora; la card lo dice en vez de mostrar un hueco.
     */
    ultimaNota: z
        .strictObject({
        tipo: z.string().max(30),
        publicadaAt: z.iso.datetime(),
        comienzo: z.string().max(200).nullable(),
    })
        .nullable(),
    /** Estado del protocolo de silencio. Operativo, sin color. */
    silencio: z.strictObject({
        enCurso: z.boolean(),
        /** Desde cuándo está en curso el episodio. `null` sin episodio. */
        desdeAt: z.iso.datetime().nullable(),
        nivelEjecutado: z.number().int().min(0).max(2),
        agotado: z.boolean(),
        /**
         * La consulta del nivel 2 a la persona de contacto, si salió en este
         * episodio (devolución del 04/10: el aviso de la ficha lo dice). Está sin
         * responder por construcción: la respuesta del cuidador es un contacto y
         * cierra el episodio (R-04).
         */
        consultaAlCuidador: z.strictObject({ enviadaAt: z.iso.datetime() }).nullable(),
    }),
    patronCadencia: z.string().max(20),
    extensionDias: z.number().int().nullable(),
    /**
     * La identidad descifrada, para quien ve al paciente (ADR-020). La edad es la
     * de hoy, calculada al descifrar la fecha de nacimiento; la fecha no viaja.
     */
    identidad: z
        .strictObject({
        nombres: z.string(),
        apellidos: z.string(),
        tipoDocumento: z.string(),
        numeroDocumento: z.string(),
        paisEmision: z.string().nullable(),
        edad: z.number().int().nonnegative(),
        sexoSegunDocumento: z.enum(['F', 'M', 'X']),
    })
        .optional(),
});
// ── M-06 · Tab Timeline · M-08 · Tab Señales PIA ────────────────────────────
/**
 * Un evento del timeline.
 *
 * Sale de `clinical_event` y **de nada más**: es la regla de §9.3 aplicada a la
 * lectura. Si el workspace necesitara mostrar algo que no está acá, lo que falta
 * es un tipo en el catálogo, no una consulta a una tabla de estado.
 *
 * `resumen` se arma en el servidor a partir del payload —que es cerrado y sin
 * texto libre—, así que es siempre una frase construida con códigos y números,
 * nunca algo que escribió una persona.
 */
export const eventoDelTimelineSchema = z.strictObject({
    id: z.uuid(),
    tipo: tipoDeEventoSchema,
    grupo: z.enum(['escala', 'checkin', 'pia', 'evento', 'clinico', 'programa', 'silencio']),
    occurredAt: z.iso.datetime(),
    fase: faseSchema.nullable(),
    resumen: z.string().max(200),
    /** Si es un hito de evaluación o una teleconsulta: se destacan en la lectura. */
    destacado: z.boolean(),
    /**
     * La aplicación del formulario que este evento cerró, en un check-in o una
     * escala respondidos (DEMO 06/10): con ella la pantalla pide "Ver respuestas".
     * La resuelve el servidor; si no la encuentra —un evento viejo, uno sin
     * aplicación—, no viene, y el evento se muestra como siempre.
     */
    aplicacionId: z.uuid().optional(),
});
export const timelineDelCasoSchema = z.strictObject({
    eventos: z.array(eventoDelTimelineSchema),
    /** Cuántos quedaron sin traer. La lista es larga: son 365 días. */
    hayMas: z.boolean(),
});
export const GRUPOS_DEL_TIMELINE = [
    'escala',
    'checkin',
    'pia',
    'evento',
    'clinico',
    'programa',
    'silencio',
];
export const filtrosDelTimelineSchema = z.strictObject({
    grupo: z.enum(GRUPOS_DEL_TIMELINE).optional(),
    limite: z.coerce.number().int().min(1).max(200).optional(),
});
// ── Tab Cuestionarios (E3.25, D-01) ─────────────────────────────────────────
/**
 * Un cuestionario respondido, tal como quedó guardado: su formulario, su hito y
 * las respuestas por `linkId`, que son **códigos y números**, nunca texto de la
 * persona. La pantalla las lee contra la definición del formulario, que viaja
 * compilada en la PWA; los hechos (medicación, reinternaciones, reintervenciones)
 * y la comparación de D+360 los calculan las mismas funciones puras que usa la
 * API (`hechosDelCuestionario`, `comparacionDelCierreAnual`).
 */
export const cuestionarioRespondidoSchema = z.strictObject({
    id: z.uuid(),
    formularioId: z.string().max(30),
    hito: z.string().max(20).nullable(),
    respondidoAt: z.iso.datetime(),
    /** Quién respondió: la persona, su cuidador o el equipo (sólo la medicación al egreso). */
    respondidoPor: z.enum(['paciente', 'cuidador', 'equipo']),
    respuestas: z.record(z.string(), respuestaSchema),
});
export const cuestionariosDelCasoSchema = z.strictObject({
    cuestionarios: z.array(cuestionarioRespondidoSchema),
    /**
     * Cómo se llama cada droga del catálogo, vigente o retirada, por su código:
     * una respuesta de hace seis meses se sigue leyendo aunque la entrada se haya
     * retirado después.
     */
    medicamentos: z.record(z.string(), z.string()),
});
// ── Historia · las respuestas de un check-in o una escala (DEMO 06/10) ──────
/**
 * Lo que la persona respondió en una aplicación, tal como quedó guardado: el
 * formulario y su versión, y las respuestas por `linkId`, que son **códigos y
 * números**, nunca texto de la persona. La pantalla las lee contra la definición
 * del formulario, que viaja en la PWA, como la pestaña Cuestionarios. No trae
 * puntaje ni color: el puntaje ya está en el evento, y esto no pasa por el motor.
 */
export const respuestasDeAplicacionSchema = z.strictObject({
    id: z.uuid(),
    formularioId: z.string().max(30),
    formularioVersion: z.string().max(20),
    hito: z.string().max(20).nullable(),
    /** Si quedó completa o a medias: en una parcial se dice qué quedó sin responder. */
    estado: z.enum(['completa', 'parcial']),
    /** Quién respondió: la persona o su cuidador en su nombre. */
    respondidoPor: z.enum(['paciente', 'cuidador', 'equipo']),
    respuestas: z.record(z.string(), respuestaSchema),
});
// ── M-09 · Tab Notas ────────────────────────────────────────────────────────
export const notaDelCasoSchema = z.strictObject({
    id: z.uuid(),
    tipo: z.enum(['evolucion', 'post-consulta', 'plan-terapeutico', 'observacion']),
    estado: z.enum(['borrador', 'publicada']),
    autorAlias: z.string().max(80),
    createdAt: z.iso.datetime(),
    publicadaAt: z.iso.datetime().nullable(),
    /**
     * El texto de la nota, venga de donde venga.
     *
     * Un borrador lo trae de Postgres; una publicada, de la copia local mientras
     * la proyección no se confirmó y del store cuando ya está allá (ADR-002). Es
     * `null` en un solo caso: la nota está en el store y el store no respondió
     * ahora. La pantalla lo dice y se vuelve a intentar — nunca se calla.
     *
     * **Los borradores ajenos no llegan hasta acá**: el servicio no los devuelve
     * (H-8). Si una nota está en la lista en estado borrador, es de quien mira.
     */
    texto: z.string().nullable(),
    /**
     * El `DocumentReference`. `null` en un borrador, y también en una publicada
     * cuya proyección sigue pendiente — que es cuando la pantalla puede decir, con
     * verdad, que está en cola.
     */
    fhirDocumentReferenceId: z.string().max(64).nullable(),
});
export const crearNotaSchema = z.strictObject({
    tipo: z.enum(['evolucion', 'post-consulta', 'plan-terapeutico', 'observacion']),
    texto: z.string().min(1).max(8000),
    /** Guardar como borrador (M-18) o publicar directo. */
    publicar: z.boolean(),
});
// ── M-10 · Tab Acciones ─────────────────────────────────────────────────────
export const decidirContinuidadSchema = z.strictObject({
    decision: z.enum(['continuar', 'extender', 'derivar', 'alta_anticipada']),
    hito: z.string().min(1).max(20),
});
// ── M-23 · Vista del coordinador ────────────────────────────────────────────
/**
 * Lo que ve el coordinador · **sin escalamientos**.
 *
 * El wireframe M-23 nació como bandeja de escalamientos ("recibe escalamientos
 * cuando alertas rojas no son atendidas dentro de las 4h") y esa parte quedó
 * derogada junto con el motor de alertas — el propio HTML lo marca. Lo que
 * sobrevive es lo que el rol necesita igual: la **operación de la institución**,
 * agregada y cross-paciente. El coordinador es operativo, no clínico
 * (`workspace-clinico.html`), y desde el 06/08 además es **del hospital**, no de
 * Fundación.
 */
export const operacionDeLaInstitucionSchema = z.strictObject({
    cohorte: cohorteSchema,
    /** Casos cuyo protocolo de silencio se agotó. Registro, no aviso a nadie. */
    silencioAgotado: z.number().int().nonnegative(),
    /** Contactos del plan que vencieron sin respuesta en los últimos 7 días. */
    contactosPerdidos7d: z.number().int().nonnegative(),
    /** Programas que entran a su hito de evaluación en los próximos 7 días. */
    evaluacionesProximas7d: z.number().int().nonnegative(),
    /** Notas clínicas publicadas en los últimos 7 días. Actividad del equipo. */
    notasPublicadas7d: z.number().int().nonnegative(),
    /** Los casos con más días sin contacto. Sin color y sin acción sugerida. */
    masSilenciosos: z.array(z.strictObject({
        programaId: z.uuid(),
        alias: z.string().max(80),
        diasSinContacto: z.number().int().nonnegative().nullable(),
        fase: faseSchema,
        /** Se agotó el protocolo de silencio de su fase, igual que en la lista. */
        sinContacto: z.boolean(),
    })),
});
//# sourceMappingURL=clinico.js.map